import { useState } from 'react';
import { values } from 'ramda';
import { InputActionMeta } from 'react-select';
import { useGoogleAddress } from '~/App/shared/hooks/useGoogleAddressHook';
import { useDynamicScript } from '~/App/shared/hooks/useDynamicScript';
import { googleMapsApiSource } from '~/config/dynamicScriptsTags';
import { OptionType } from '~/App/shared/types/OptionType';
import { DeliveryLocation } from '~/App/shared/interfaces/DeliveryLocation';

export interface AddressAutocompleteState {
  value: string;
  address: DeliveryLocation | null;
  options: OptionType[];
  inputValue: string;
  isAddressValid: boolean;
  handleAddressSelect: (option: OptionType) => void;
  handleInputChange: (value: string, actionMeta: InputActionMeta) => void;
  handleOnBlur: () => void;
  handleMenuOpen: () => void;
  handleMenuClose: () => void;
  reset: () => void;
}

export const useAddressAutocomplete = (): AddressAutocompleteState => {
  const { onChange, value, address, options, setOption } = useGoogleAddress();
  useDynamicScript(googleMapsApiSource);

  const [inputValue, setInputValue] = useState('');

  const handleAddressSelect = (option: OptionType) => {
    setOption(option);
    onChange(option?.label ?? '');
  };

  const handleInputChange = (
    nextValue: string,
    actionMeta: InputActionMeta
  ) => {
    if (actionMeta.action !== 'input-change') {
      return;
    }
    onChange(nextValue);
    setInputValue(nextValue);
  };

  const handleOnBlur = () => {
    if (value === options?.[0]?.label) {
      handleAddressSelect(options[0]);
    }
  };

  const handleMenuOpen = () => {
    if (value) {
      setInputValue(value);
    }
  };

  const handleMenuClose = () => setInputValue('');

  const reset = () => {
    setOption(null);
    onChange('');
    setInputValue('');
  };

  const isAddressValid = values(address || {}).filter(Boolean).length === 8;

  return {
    value,
    address,
    options,
    inputValue,
    isAddressValid,
    handleAddressSelect,
    handleInputChange,
    handleOnBlur,
    handleMenuOpen,
    handleMenuClose,
    reset
  };
};
